"use client";

import { useEffect, useRef, useState } from "react";
import { replacePlaceholders, scrollTo } from "@repo/shared-utils/common-utils";
import { StickyHeaderProps } from "./AMCStickyHeader.interface";
import AmcHideOnLogin from "../AMCHideOnLogin/AmcHideOnLogin";
import { recordAmcPdpCtaMetric } from "../../monitoring/amcPdpMonitoringHelpers";

/** Strips leading # from navigation IDs for DOM queries */
const sanitizeId = (id?: string): string => {
  if (!id) return "";
  return id.startsWith("#") ? id.substring(1) : id;
};

const STICKY_HEADER_SELECTOR = '[data-cmp="AMCStickyHeader"]';
/** Small slack so activation feels "near" the sticky line, not pixel-perfect */
const STICKY_LINE_EPSILON_PX = 8;

const getStickyHeaderElement = () =>
  document.querySelector(STICKY_HEADER_SELECTOR) as HTMLElement | null;

const getSiteHeaderOffset = (version?: string) => {
  const isApp = version !== "in";
  if (isApp) return 75;
  return window.innerWidth < 768 ? 130 : 85;
};

const getScrollOffset = (version?: string) => {
  const stickyHeaderElement = getStickyHeaderElement();
  const stickyHeaderHeight = stickyHeaderElement?.offsetHeight ?? 0;
  return stickyHeaderHeight + getSiteHeaderOffset(version);
};

/**
 * Last nav section whose top has reached / crossed the sticky header line.
 * Sections still below mid-viewport do not activate.
 */
const getActiveSectionId = (sectionIds: string[], version?: string): string | null => {
  const stickyLine = getScrollOffset(version) + STICKY_LINE_EPSILON_PX;
  let activeId: string | null = null;

  for (const id of sectionIds) {
    const section = document.getElementById(id);
    if (!section) continue;

    if (section.getBoundingClientRect().top <= stickyLine) {
      activeId = id;
    }
  }

  return activeId;
};

const getSectionIdsFromStickyData = (
  stickyHeaderData: StickyHeaderProps["stickyHeaderData"]
): string[] => {
  if (!stickyHeaderData) return [];

  return Object.values(stickyHeaderData)
    .map((item) => sanitizeId(item?.navigationId))
    .filter(Boolean);
};

const shouldBeSticky = () => window?.scrollY > 100;

const toggleStickyClasses = (isSticky: boolean) => {
  const wrapper = document.querySelector(".content-wrapper") as HTMLElement | null;
  const leftSection = document.querySelector(".left-section") as HTMLElement | null;

  if (!wrapper && !leftSection) return;

  if (isSticky) {
    wrapper?.classList.add("relative", "z-5");
    leftSection?.classList.add("mt-6");
  } else {
    wrapper?.classList.remove("relative", "z-5");
    leftSection?.classList.remove("mt-6");
  }
};

const getScrollPosition = (section: HTMLElement, version?: string) => {
  const totalOffset = getScrollOffset(version);
  const elementPosition = section.getBoundingClientRect().top + window.scrollY;
  return Math.max(0, elementPosition - totalOffset);
};

const AMCStickyHeader = ({ version, stickyHeaderData, amcDetails }: StickyHeaderProps) => {
  const [activeTab, setActiveTab] = useState("performance");
  const [isSticky, setIsSticky] = useState(false);

  const isClickingRef = useRef(false);
  const activeTabRef = useRef(activeTab);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    const handleStickyVisibility = () => {
      setIsSticky(shouldBeSticky());
    };

    window.addEventListener("scroll", handleStickyVisibility);
    handleStickyVisibility();
    return () => window?.removeEventListener("scroll", handleStickyVisibility);
  }, []);

  useEffect(() => {
    const sectionIds = getSectionIdsFromStickyData(stickyHeaderData);

    const syncActiveTabFromScroll = () => {
      if (isClickingRef.current) return;

      const nextActiveId = getActiveSectionId(sectionIds, version);
      if (!nextActiveId || nextActiveId === activeTabRef.current) return;

      setActiveTab(nextActiveId);

      const activeButton = document.querySelector<HTMLButtonElement>(
        `button[data-id="${nextActiveId}"]`
      );

      if (activeButton) {
        activeButton.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    };

    const handleScroll = () => {
      if (rafIdRef.current != null) return;

      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        syncActiveTabFromScroll();
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    syncActiveTabFromScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafIdRef.current != null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [stickyHeaderData, version]);

  useEffect(() => {
    toggleStickyClasses(isSticky);
  }, [isSticky]);

  const handleTabClick = (id: string, tabLabel?: string) => {
    const sectionId = sanitizeId(id);
    const section = document.getElementById(sectionId);
    if (section) {
      const targetPosition = getScrollPosition(section, version);

      isClickingRef.current = true;
      activeTabRef.current = sectionId;
      setActiveTab(sectionId);

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });

      setTimeout(() => {
        isClickingRef.current = false;
      }, 800);

      setTimeout(() => {
        const sectionRect = section.getBoundingClientRect();
        const buffer = 10;
        const totalOffset = getScrollOffset(version);

        if (sectionRect.top < totalOffset - buffer) {
          const adjustment = totalOffset - sectionRect.top + buffer;

          window.scrollTo({
            top: window.scrollY - adjustment,
            behavior: "smooth",
          });
        }
      }, 400);
    }

    const button = document.querySelector<HTMLButtonElement>(`button[data-id="${sectionId}"]`);
    if (button) {
      button.scrollIntoView({ behavior: "smooth", inline: "center" });
    }

    if (section) {
      recordAmcPdpCtaMetric("sticky_header_tab_scroll", {
        componentName: "AMCStickyHeader",
        ctaName: tabLabel,
        success: true,
        extra: { section_id: sectionId },
      });
    }
  };

  type StickyTabItem = {
    navigationId?: string;
    navigationTitleText?: string;
  };

  const renderStickyButton = (
    item: StickyTabItem,
    index: number,
    isActive: boolean,
    onTabClick: (tab: string, tabLabel?: string) => void
  ) => {
    const sectionId = sanitizeId(item?.navigationId);
    const tabLabel = replacePlaceholders(
      item.navigationTitleText ?? "",
      amcDetails as unknown as Record<string, string>
    );

    return (
      <button
        key={`stickyLink-${index}`}
        data-id={sectionId}
        data-testid={`sticky-header-tab-${sectionId}`}
        data-monitor-click
        onClick={() => onTabClick(item.navigationId ?? "", tabLabel)}
        className={`mx-4 cursor-pointer py-3 text-sm leading-[19px] whitespace-nowrap md:py-6 md:text-base ${
          isActive
            ? "text-link-primary after:bg-link-primary relative font-medium after:right-0 after:bottom-[0px] after:h-[1px] after:w-[100%] md:after:absolute md:after:content-['']"
            : "text-bfl-blue"
        }`}
      >
        {tabLabel}
      </button>
    );
  };

  return (
    <div
      className={`fixed left-0 z-5 ${
        version !== "in" ? "top-[75px] md:top-[75px]" : "top-[130px] md:top-[85px]"
      } w-full bg-white shadow-lg ${!isSticky ? "hidden" : ""}`}
      data-cmp="AMCStickyHeader"
      data-cmp-type="react"
      data-cmp-title="Sticky Header"
    >
      <nav className="flex-between-center">
        <div className="flex-between-center no-scrollbar w-[80%] overflow-scroll xl:overflow-hidden">
          {Object.values(stickyHeaderData ?? {}).map((item, index) => {
            const protectedTabs = ["YouAreHere", "RelatedLinks"];

            const sectionId = sanitizeId(item?.navigationId);
            const isProtectedTab = protectedTabs?.includes(sectionId);

            const isActive = activeTab === sectionId;

            return isProtectedTab ? (
              <AmcHideOnLogin key={item?.navigationTitleText}>
                {renderStickyButton(item, index, isActive, handleTabClick)}
              </AmcHideOnLogin>
            ) : (
              renderStickyButton(item, index, isActive, handleTabClick)
            );
          })}
        </div>
        <button
          data-testid="sticky-header-scroll-top"
          data-monitor-click
          onClick={() => scrollTo(0, "smooth")}
          className="cursor-pointer border-l border-gray-400 bg-orange-100 px-[10px] py-2 md:px-6 md:py-5"
        >
          <i className="icomoon text-sm before:content-['\e952']"></i>
        </button>
      </nav>
    </div>
  );
};

export default AMCStickyHeader;
